/**
 * XUONG LAP LOI.
 *
 * Phan con lai cua ung dung chi duoc import tu day. Khi goi WASM cua loi Rust
 * co mat (@pdfeditor/core-wasm), no duoc dung; neu khong, ta chay ban gia lap
 * bang pdf.js. Doi loi KHONG duoc keo theo bat ky thay doi nao o tang giao dien.
 */
import type { DocumentId, PdfCore } from '@pdfeditor/types';
import { PdfCoreMock } from './PdfCoreMock';
import { PdfCoreBrowser } from './PdfCoreBrowser';
import type { PDFPageProxy } from './pdfjs';

export type CoreFlavor = 'wasm' | 'browser-pdflib' | 'mock-pdfjs';

export interface CoreHandle {
  core: PdfCore;
  flavor: CoreFlavor;
  /** Mo ta ngan gon de hien tren thanh trang thai. */
  label: string;
}

/**
 * Nguon trang cho tang hien thi.
 *
 * Lop canvas va lop van ban can doi tuong trang cua pdf.js de ve va de lay
 * getTextContent(). Day la cua so ro ri CO CHU DICH va duoc dat sau mot giao
 * dien rieng: khi loi WASM san sang, ban hien thuc cua no tra ve null o day
 * va tang hien thi chuyen sang duong renderPage() cua loi.
 */
export interface WebPageSource {
  getPageProxy(doc: DocumentId, pageIndex: number): Promise<PDFPageProxy | null>;
}

export function asPageSource(core: PdfCore): WebPageSource | null {
  const candidate = core as unknown as Partial<WebPageSource>;
  return typeof candidate.getPageProxy === 'function' ? (candidate as WebPageSource) : null;
}

let cached: CoreHandle | null = null;

/**
 * Lay loi. Thu nap ban WASM truoc, that bai thi lui ve ban gia lap.
 *
 * Luu y ve import dong: ten goi duoc dat trong bien de cong cu dong goi khong
 * co gang giai no khi build - goi WASM chua ton tai trong cay nguon nay.
 */
export async function getCore(): Promise<CoreHandle> {
  if (cached) return cached;

  // Chi thu nap WASM khi duoc bat tuong minh (VITE_PDF_CORE=wasm). Neu khong
  // co co nay, mot lan import that bai se de lai loi mang do trong console moi
  // lan khoi dong - gay nhieu cho viec go loi that.
  if (import.meta.env['VITE_PDF_CORE'] === 'wasm') {
    const wasmPackage = '@pdfeditor/core-wasm';
    try {
      const mod = (await import(/* @vite-ignore */ wasmPackage)) as {
        createCore?: () => Promise<PdfCore>;
      };
      if (typeof mod.createCore === 'function') {
        const core = await mod.createCore();
        cached = { core, flavor: 'wasm', label: 'Lõi Rust/PDFium (WASM)' };
        return cached;
      }
    } catch (err) {
      console.warn('[core] khong nap duoc goi WASM, lui ve ban gia lap:', err);
    }
  }

  // Mac dinh la loi trinh duyet SUA DUOC (pdf.js de doc + pdf-lib de ghi).
  //
  // Ban gia lap chi doc van con nguyen ven ngay canh - test dung no de kiem lop
  // doc ma khong keo theo pdf-lib, va no la moc so sanh khi mot lan sua bi nghi
  // ngo la do duong ghi gay ra. Nhung no khong con la mac dinh: nguoi dung mo
  // ung dung len la phai sua duoc.
  cached = {
    core: new PdfCoreBrowser(),
    flavor: 'browser-pdflib',
    label: 'Lõi trình duyệt — sửa được chữ, vẽ đè (không dùng để che thông tin)',
  };
  return cached;
}

/** Chi dung cho test va cho viec doi chieu khi go loi duong ghi. */
export function createMockCore(): CoreHandle {
  return {
    core: new PdfCoreMock(),
    flavor: 'mock-pdfjs',
    label: 'Lõi giả lập pdf.js — chỉ đọc',
  };
}

export { PdfCoreMock, PdfCoreBrowser };
